import { action, computed, makeObservable, observable } from 'mobx';

export class VideoPlayerStore {
  @observable
  private _currentTime: number = 0;

  @observable
  private _duration: number = 0;

  @observable
  private _isPlaying: boolean = false;

  @observable
  private _isFullscreen: boolean = false;

  @observable
  private _isMuted: boolean = false;

  @observable
  private _volume: number = 100;

  @observable
  private _prevVolume: number = 100;

  @observable
  private _mode: 'moment' | 'full' = 'moment';

  @observable
  private _preview: string | null = null;

  @observable
  private _moment: {
    start: number;
    end: number;
  } | null = null;

  // A set rather than a boolean: the filmstrip's trim handles and the scrub bar's range drag
  // are two independent gestures that can both be live at once (one finger on each, on a
  // touch-capable layout), and a bare boolean lets either one's release clear a flag the other
  // still needs. Keying by source keeps one gesture's end from clobbering the other's.
  @observable
  private _draggingSources = new Set<string>();

  constructor() {
    makeObservable(this);
  }

  @action
  setCurrentTime(time: number) {
    this._currentTime = time;
  }

  @action
  setPreview(preview: string | null) {
    this._preview = preview;
  }

  @computed
  get preview() {
    return this._preview;
  }

  @computed
  get currentTime() {
    return this._currentTime;
  }

  @action
  setMode(mode: 'moment' | 'full') {
    this._mode = mode;
  }

  @computed
  get mode() {
    return this._mode;
  }

  @action
  setDuration(duration: number) {
    this._duration = duration;
  }

  @computed
  get duration() {
    return this._duration;
  }

  @action
  setIsFullscreen(isFullscreen: boolean) {
    this._isFullscreen = isFullscreen;
  }

  @computed
  get isFullscreen() {
    return this._isFullscreen;
  }

  @action
  setIsPlaying(isPlaying: boolean) {
    this._isPlaying = isPlaying;
  }

  @computed
  get isPlaying() {
    return this._isPlaying;
  }

  @action
  setIsMuted(isMuted: boolean) {
    this._isMuted = isMuted;
  }

  @computed
  get isMuted() {
    return this._isMuted;
  }

  @action
  setVolume(volume: number) {
    this._volume = volume;
  }

  @computed
  get volume() {
    return this._volume;
  }

  @action
  setPrevVolume(prevVolume: number) {
    this._prevVolume = prevVolume;
  }

  @computed
  get prevVolume() {
    return this._prevVolume;
  }

  @action
  setMoment(moment: { start: number; end: number } | null) {
    this._moment = moment;
  }

  @computed
  get moment() {
    return this._moment;
  }

  @action
  setIsDragging(source: string, isDragging: boolean) {
    if (isDragging) {
      this._draggingSources.add(source);
    } else {
      this._draggingSources.delete(source);
    }
  }

  @computed
  get isDragging() {
    return this._draggingSources.size > 0;
  }
}

export const videoPlayerStore = new VideoPlayerStore();
